<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useMapGetter, useStore } from 'dashboard/composables/store';
import { useTemplateVariableMapping } from 'dashboard/composables/useTemplateVariableMapping';
import { useWhatsAppInboxTemplatesSync } from 'dashboard/composables/useWhatsAppInboxTemplatesSync';
import {
  COMPONENT_TYPES,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import {
  DEFAULT_APPOINTMENT_REMINDER_TEMPLATE_NAME,
  defaultProcessedParamsForTemplate,
  findDefaultAppointmentReminderTemplateOption,
} from 'dashboard/helper/appointmentReminderDefaults';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  followUp: {
    type: Object,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const store = useStore();

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const inboxIdRef = computed(() => props.inbox.id);

const filteredWhatsAppTemplates = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  inboxIdRef
);
const filteredTwilioTemplates = useFunctionGetter(
  'inboxes/getFilteredTwilioTemplates',
  inboxIdRef
);

const variableSelections = ref({});

const isTwilioWhatsAppInbox = computed(
  () =>
    props.inbox.channel_type === 'Channel::TwilioSms' &&
    props.inbox.medium === 'whatsapp'
);

const { isSyncingTemplates } = useWhatsAppInboxTemplatesSync(
  inboxIdRef,
  id => store.getters['inboxes/getInbox'](id)
);

const templateOptions = computed(() => {
  const templates = isTwilioWhatsAppInbox.value
    ? filteredTwilioTemplates.value
    : filteredWhatsAppTemplates.value;

  return templates.map(template => {
    const templateName =
      template.friendly_name || template.name || 'Unnamed Template';
    const friendlyName = templateName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, letter => letter.toUpperCase());

    return {
      value: templateName,
      label: friendlyName,
      template,
    };
  });
});

const getTemplateBody = template => {
  if (!template) return '';

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  if (bodyComponent?.text) return bodyComponent.text;
  if (template.body) return template.body;
  if (template.text) return template.text;
  if (template.content) return template.content;

  return '';
};

const {
  variableMappingOptions,
  extractTemplateVariables,
  isStaticMode: _isStaticMode,
} = useTemplateVariableMapping({
  i18nBaseKey: 'INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.VARIABLE_OPTIONS',
  contactAttributes,
  includeAppointmentVariables: true,
  variablePattern: /\{\{([^}]+)\}\}/g,
});

const normalizeLanguage = language =>
  (language || '').replace('-', '_').toLowerCase();

const languagesMatch = (left, right) => {
  if (!left || !right) return true;

  const a = normalizeLanguage(left);
  const b = normalizeLanguage(right);

  return a === b || a.split('_')[0] === b.split('_')[0];
};

const findTemplateOptionFromConfig = () => {
  if (!props.followUp.template_name) return null;

  return (
    templateOptions.value.find(option => {
      const template = option.template;
      if (isTwilioWhatsAppInbox.value) {
        return option.value === props.followUp.template_name;
      }

      return (
        template.name === props.followUp.template_name &&
        languagesMatch(template.language, props.followUp.template_language)
      );
    }) || null
  );
};

const selectedTemplate = computed(
  () => findTemplateOptionFromConfig()?.template || null
);

const selectedTemplateBody = computed(() =>
  getTemplateBody(selectedTemplate.value)
);

const getTemplateVariableKeys = template =>
  extractTemplateVariables(getTemplateBody(template)).map(key => key.trim());

const templateVariables = computed(() => {
  if (!selectedTemplate.value) return [];
  return getTemplateVariableKeys(selectedTemplate.value);
});

const isStaticMode = varKey =>
  _isStaticMode(variableSelections.value, varKey);

const patchFollowUp = patch => {
  emit('update', patch);
};

const hydrateVariableSelections = () => {
  const mappings = props.followUp.processed_params || {};
  const knownPatterns = [
    /^\{\{\s*contact\.\w+\s*\}\}$/,
    /^\{\{\s*contact\.custom_attribute\.\w+\s*\}\}$/,
    /^\{\{\s*appointment\.\w+\s*\}\}$/,
  ];

  const selections = {};
  Object.entries(mappings).forEach(([key, value]) => {
    if (!value) return;
    if (knownPatterns.some(pattern => pattern.test(value))) {
      selections[key] = value;
    } else {
      selections[key] = '__static__';
    }
  });

  variableSelections.value = selections;
};

const buildProcessedParamsForTemplate = (template, existing = {}, { applyDefaults = false } = {}) => {
  const vars = getTemplateVariableKeys(template);
  const templateName = template.friendly_name || template.name;
  const defaults =
    applyDefaults && templateName === DEFAULT_APPOINTMENT_REMINDER_TEMPLATE_NAME
      ? defaultProcessedParamsForTemplate(templateName) || {}
      : {};

  const mappings = {};
  vars.forEach(varKey => {
    mappings[varKey] = existing[varKey] || defaults[varKey] || '';
  });

  return mappings;
};

const ensureProcessedParamsForTemplate = template => {
  if (!template) return;

  const existing = props.followUp.processed_params || {};
  const templateName = template.friendly_name || template.name;
  const mappings = buildProcessedParamsForTemplate(template, existing, {
    applyDefaults: templateName === DEFAULT_APPOINTMENT_REMINDER_TEMPLATE_NAME,
  });
  const hasChanges =
    JSON.stringify(mappings) !== JSON.stringify(existing);

  if (hasChanges) {
    patchFollowUp({ processed_params: mappings });
  }

  hydrateVariableSelections();
};

const clearTemplateSelection = () => {
  patchFollowUp({
    template_name: '',
    template_language: '',
    template_category: '',
    template_namespace: '',
    processed_params: {},
  });
  variableSelections.value = {};
};

const applyTemplate = (templateOption, { applyDefaults = false } = {}) => {
  if (!templateOption?.template) {
    clearTemplateSelection();
    return;
  }

  const template = templateOption.template;
  const processedParams = buildProcessedParamsForTemplate(
    template,
    props.followUp.processed_params || {},
    { applyDefaults }
  );

  if (isTwilioWhatsAppInbox.value) {
    patchFollowUp({
      template_name: template.friendly_name || template.name,
      template_language: template.language || 'en',
      template_category: template.category || 'UTILITY',
      template_namespace: '',
      processed_params: processedParams,
    });
  } else {
    patchFollowUp({
      template_name: template.name,
      template_language: template.language,
      template_category: template.category,
      template_namespace: template.namespace || '',
      processed_params: processedParams,
    });
  }

  hydrateVariableSelections();
};

const handleTemplateChange = templateName => {
  const templateOption = templateOptions.value.find(
    option => option.value === templateName
  );
  applyTemplate(templateOption, {
    applyDefaults: templateName === DEFAULT_APPOINTMENT_REMINDER_TEMPLATE_NAME,
  });
};

const handleVariableSelect = (varKey, val) => {
  variableSelections.value = {
    ...variableSelections.value,
    [varKey]: val,
  };

  patchFollowUp({
    processed_params: {
      ...(props.followUp.processed_params || {}),
      [varKey]: val === '__static__' ? '' : val,
    },
  });
};

const handleStaticValueChange = (varKey, value) => {
  patchFollowUp({
    processed_params: {
      ...(props.followUp.processed_params || {}),
      [varKey]: value,
    },
  });
};

const maybeApplyDefaultTemplate = () => {
  if (props.followUp.template_name) return;

  const templateOption = findDefaultAppointmentReminderTemplateOption(
    templateOptions.value
  );
  if (!templateOption) return;

  applyTemplate(templateOption, { applyDefaults: true });
};

const reconcileTemplateConfig = () => {
  if (isSyncingTemplates.value) return;

  const configTemplateName = props.followUp.template_name;

  if (!configTemplateName) {
    if (Object.keys(props.followUp.processed_params || {}).length > 0) {
      patchFollowUp({ processed_params: {} });
      variableSelections.value = {};
    }
    maybeApplyDefaultTemplate();
    return;
  }

  const templateOption = findTemplateOptionFromConfig();
  if (!templateOption) {
    clearTemplateSelection();
    maybeApplyDefaultTemplate();
    return;
  }

  ensureProcessedParamsForTemplate(templateOption.template);
};

watch(
  [templateOptions, isSyncingTemplates, () => props.followUp.template_name],
  reconcileTemplateConfig,
  { immediate: true }
);

watch(
  () => props.followUp.processed_params,
  () => {
    hydrateVariableSelections();
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-4 border-t border-n-weak pt-4">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        :model-value="followUp.template_name"
        :options="templateOptions"
        :placeholder="
          t('INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.PLACEHOLDER')
        "
        appearance="input"
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="handleTemplateChange"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.HELP') }}
      </p>
    </div>

    <div
      v-if="selectedTemplate && selectedTemplateBody"
      class="flex flex-col gap-1"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.BODY_LABEL') }}
      </p>
      <p
        class="whitespace-pre-wrap rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-11"
      >
        {{ selectedTemplateBody }}
      </p>
    </div>

    <div
      v-if="selectedTemplate && templateVariables.length > 0"
      class="flex flex-col gap-3"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.VARIABLES_TITLE') }}
      </p>
      <div
        v-for="varKey in templateVariables"
        :key="varKey"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center gap-3">
          <span
            class="inline-block rounded-md bg-n-alpha-black2 px-6 py-2.5 text-xs text-n-slate-12"
          >
            {{ varKey }}
          </span>
          <ComboBox
            :model-value="variableSelections[varKey] || ''"
            :options="variableMappingOptions"
            :placeholder="
              t(
                'INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.VARIABLE_SELECT_PLACEHOLDER'
              )
            "
            appearance="input"
            class="flex-1 [&>div>button]:bg-n-alpha-black2"
            @update:model-value="val => handleVariableSelect(varKey, val)"
          />
        </div>
        <div v-if="isStaticMode(varKey)" class="flex items-center gap-3">
          <span class="invisible inline-block rounded-md px-6 py-2.5 text-xs">
            {{ varKey }}
          </span>
          <Input
            :model-value="followUp.processed_params[varKey]"
            type="text"
            class="flex-1"
            :placeholder="
              t(
                'INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.STATIC_VALUE_PLACEHOLDER'
              )
            "
            @update:model-value="val => handleStaticValueChange(varKey, val)"
          />
        </div>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.APPOINTMENT_REMINDERS.TEMPLATE.VARIABLE_HINT') }}
      </p>
    </div>
  </div>
</template>
